import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import "./Home.css";
import logo from "../logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";

import { auth } from "../firebase";

const Home = () => { // Changed to const function
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // login | signup | reset
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const googleProvider = new GoogleAuthProvider();

  const [billing, setBilling] = useState("monthly");
  const [showCompare, setShowCompare] = useState(false);

  const annualMultiplier = 10;

  const mkPrice = useCallback(
    (monthlyZar) => {
      if (monthlyZar === 0) return { display: "$0", sub: "" };
      if (billing === "monthly") return { display: `$${monthlyZar}`, sub: "/month" };
      return { display: `$${monthlyZar * annualMultiplier}`, sub: "/year" };
    },
    [billing]
  );

  const startWithPlan = useCallback(
    (planKey) => {
      localStorage.setItem("kairos_pending_plan", planKey);
      navigate("/authentication");
    },
    [navigate]
  );

  // ✅ Updated Plans (USD) — includes Emails + Forms/Submissions + PAYG overages messaging
// Assumptions used for add-ons:
// - Extra emails: $0.005 per email (Free), $0.002 per email (paid plans)
// - Extra submissions: $0.02 per submission (all plans) — strong margin + discourages abuse
// - Extra forms: $2 per additional form / month (all plans)
// - AI credits are INCLUDED per plan; extra credits sold separately at $1 per 5 credits (your model)

const plans = useMemo(() => {
  return [
    {
      key: "free",
      name: "Free (PAYG)",
      price: mkPrice(0),
      note: "Start free, pay only when you exceed the included quotas.",
      bullets: [
        "1 website",
        "Unlimited pages",
        "Custom domain",
        "100MB storage",
        "200MB bandwidth",
        "Shop included (5% platform fee per sale)",
        "Forms: 1 included",
        "Submissions: 100",
        "Basic SEO",
        "Basic analytics",
        "10 credits once-off",
        "Branding On",
        "Email support",
      ],
      cta: "Get started",
      onClick: () => startWithPlan("free"),
    },
    {
      key: "startup",
      name: "Startup",
      price: mkPrice(5),
      note: "Best for a single business website that’s growing.",
      bullets: [
        "10 websites",
        "Unlimited pages",
        "10 Custom domains",
        "1GB storage",
        "2GB bandwidth",
        "Shop included (5% platform fee per sale)",
        "Forms: 10",
        "Submissions: 1,000",
        "Full SEO",
        "Full analytics",
        "70 AI credits",
        "Branding can be turned off",
        "Priority support",
      ],
      cta: "Choose Startup",
      onClick: () => startWithPlan("startup"),
    },
    {
      key: "business",
      name: "Business",
      price: mkPrice(20),
      note: "Best value for serious businesses with higher usage.",
      bullets: [
        "Unlimited websites",
        "Unlimited pages",
        "Unlimited custom domains",
        "15GB storage",
        "20GB bandwidth/",
        "Shop included (5% platform fee per sale)",
        "Forms: 200",
        "Submissions: 20,000",
        "Full SEO",
        "Full analytics",
        "150 AI creditsh",
        "Kairos branding off",
        "Higher priority support",
      ],
      cta: "Go Business",
      onClick: () => startWithPlan("business"),
    },
    {
      key: "agency",
      name: "Agency",
      price: mkPrice(50),
      note: "For developers & agencies managing many client sites.",
      bullets: [
        "Unlimited websites",
        "Unlimited pages",
        "Unlimited custom domain",
        "25GB storage",
        "50GB bandwidth",
        "Shop included (5% platform fee per sale)",
        "Forms: Unlimited",
        "Submissions: Unlimited",
        "Full SEO",
        "Full analytics",
        "300 AI credits",
        "Kairos branding off",
        "Agent priority support",
        "Best PAYG rates",
      ],
      cta: "Go Agency",
      onClick: () => startWithPlan("agency"),
    },
  ];
}, [mkPrice, startWithPlan]);

// ✅ Compare Rows — matches the plan keys above (Free/Startup/Business/Agency)
const compareRows = useMemo(() => {
  return [
    {
      label: "Websites included",
      free: "1",
      startup: "1",
      business: "Unlimited",
      agency: "Unlimited",
    },
    {
      label: "Pages included",
      free: "1 page",
      startup: "Unlimited",
      business: "Unlimited",
      agency: "Unlimited",
    },
    {
      label: "Custom domains included",
      free: "1",
      startup: "1",
      business: "10",
      agency: "Unlimited",
    },
    {
      label: "Storage included",
      free: "150MB",
      startup: "1GB",
      business: "10GB",
      agency: "25GB",
    },
    {
      label: "Bandwidth/month included",
      free: "500MB",
      startup: "3GB",
      business: "10GB",
      agency: "50GB",
    },
    {
      label: "Shop / payments",
      free: "Included (5% fee per sale)",
      startup: "Included (5% fee per sale)",
      business: "Included (5% fee per sale)",
      agency: "Included (5% fee per sale)",
    },
    {
      label: "SEO",
      free: "Basic",
      startup: "Full",
      business: "Full",
      agency: "Full",
    },
    {
      label: "Analytics",
      free: "Basic",
      startup: "Full",
      business: "Full",
      agency: "Full",
    },
    {
      label: "AI credits included",
      free: "10 (once-off)",
      startup: "70",
      business: "150",
      agency: "300",
    },
    {
      label: "Forms included",
      free: "1",
      startup: "3",
      business: "10",
      agency: "50",
    },
    {
      label: "Form submissions included",
      free: "500 / month",
      startup: "1,000 / month",
      business: "5,000 / month",
      agency: "20,000 / month",
    },
    {
      label: "Branding",
      free: "Kairos branding ON",
      startup: "Can be turned OFF",
      business: "Kairos branding OFF",
      agency: "Kairos branding OFF",
    },
    {
      label: "Support",
      free: "Email support",
      startup: "Priority support",
      business: "Higher priority support",
      agency: "Agent priority support",
    },

    // ✅ Suggested PAYG Add-ons (same across plans, but Free is higher for emails)
    {
      label: "Extra submissions (PAYG)",
      free: "$0.02 / submission",
      startup: "$0.02 / submission",
      business: "$0.02 / submission",
      agency: "$0.02 / submission",
    },
    {
      label: "Extra forms (PAYG)",
      free: "$1 / form / month",
      startup: "$1 / form / month",
      business: "$0.5 / form / month",
      agency: "$0.5 / form / month",
    },
    {
      label: "Extra AI credits",
      free: "$0.2 per credit",
      startup: "$0.2 per 5 credits",
      business: "$0.09 per credit",
      agency: "$0.09 per credit",
    },
  ];
  }, []);

  const handleOAuth = async (provider) => {
    try {
      setLoading(true);
      setError("");
      await signInWithPopup(auth, provider);
      navigate("/workspaces");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    if (password.length < 6 && mode !== "reset") {
      setError("Password must be at least 6 characters long.");
      return;
    }
  
    if (mode === "signup" && !name.trim()) {
      setError("Please enter your name.");
      return;
    }
  
    try {
      setLoading(true);
      setError("");
      setMessage("");
  
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
        navigate("/workspaces");
      }
  
      if (mode === "signup") {
        // ✅ Create user
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
  
        // ✅ Set displayName on the newly created auth user
        await updateProfile(userCredential.user, {
          displayName: name,
        });
  
        navigate("/workspaces");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };  

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="home">

      {/* HERO / SIGN IN */}
      <section id="home" className="hero">
        <div className="hero-content">
          <img src={logo} alt="Hero Logo" className="hero-logo" />

          <div>
            <h1>
              <span className="gradient-text">Build</span> &{" "}
              <span className="gradient-text">Edit</span> your websites by{" "}
              <span className="gradient-text">chatting</span> with{" "}
              <span className="gradient-text">Kairos</span>
            </h1>
            <h2>
              Your next website is one{" "}
              <span className="gradient-text">Chat</span>{" "}
              <span className="gradient-text">Conversation</span> away.
            </h2>
          </div>

          {/* AUTH BOX */}
          <div className="auth-box">
            {mode !== "reset" && (
              <>
                <button
                  className="auth google"
                  onClick={() => handleOAuth(googleProvider)}
                  disabled={loading}
                >
                  Continue with Google
                </button>

                <div className="divider">or</div>
              </>
            )}

            {mode !== "reset" && mode === "signup" && (
              <input
                type="text"
                placeholder="Display Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}

            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {mode !== "reset" && (
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div> 
            )}

            {mode === "reset" ? (
              <button
                className="auth email"
                onClick={handlePasswordReset}
                disabled={loading || !email}
              >
                Send reset link
              </button>
            ) : (
              <button
                className="auth email"
                onClick={handleEmailAuth}
                disabled={loading || !email || !password}
              >
                {mode === "login" ? "Continue with Email" : "Create Account"}
              </button>
            )}

            {error && <p className="error">{error}</p>}
            {message && <p className="success">{message}</p>}

            {/* TOGGLES */}
            <div className="auth-links">
              {mode === "login" && (
                <>
                  <button onClick={() => setMode("reset")}>
                    Forgot password?
                  </button>
                  <button onClick={() => setMode("signup")}>
                    Create an account
                  </button>
                </>
              )}

              {mode === "signup" && (
                <button onClick={() => setMode("login")}>
                  Already have an account?
                </button>
              )}

              {mode === "reset" && (
                <button onClick={() => setMode("login")}>
                  Back to login
                </button>
              )}
            </div>

            <p>By Logging In or Creating an account you agree to the below policies</p>

            {/* LEGAL LINKS (Landing page footer) */}
            <div className="legal-links">
              <button
                className="legal-link-btn"
                onClick={() => navigate("/privacy-policy")}
                type="button"
              >
                Privacy Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/terms-and-conditions")}
                type="button"
              >
                Terms & Conditions
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/cookie-policy")}
                type="button"
              >
                Cookie Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/refund-policy")}
                type="button"
              >
                Refund Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/acceptable-use-policy")}
                type="button"
              >
                Acceptable Use
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/ai-use-policy")}
                type="button"
              >
                AI Use & Content Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/subscription-policy")}
                type="button"
              >
                Subscription Policy
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features">
        <h2>What <span className="gradient-text">Kairos</span> can do:</h2>

        <div className="feature-grid">
          <div className="card">
            <h3>
              <span className="gradient-text">Describe</span>
            </h3>
            <p>
              Describe your idea in natural language and <span className="gradient-text">Kairos</span> builds it, in minutes if not seconds.
            </p>
          </div>

          <div className="card">
            <h3>
              <span className="gradient-text">Chat</span>
            </h3>
            <p>
              Chat with <span className="gradient-text">Kairos</span> and get instant live editing feedback. You can change colors, text, images, with a live chat.
            </p>
          </div>

          <div className="card">
            <h3>
              <span className="gradient-text">Kairos</span>
            </h3>
            <p>
              Publish instantly, and your website will be live and you can share with your clients or on your posters.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing">
        <h2>
          Our <span className="gradient-text">Pricing</span> Plans
        </h2>

        <div className="billing-toggle-wrap">
          <div className="billing-toggle" role="group" aria-label="Billing period toggle">
            <button
              className={billing === "monthly" ? "billing-btn active" : "billing-btn"}
              onClick={() => setBilling("monthly")}
              type="button"
            >
              Monthly
            </button>
            <button
              className={billing === "annual" ? "billing-btn active" : "billing-btn"}
              onClick={() => setBilling("annual")}
              type="button"
            >
              Annual <span className="billing-badge">2 months free</span>
            </button>
          </div>
          <p className="billing-subtext">
            Annual billing is charged once per year and includes a discount equivalent to 2 months free.
          </p>
        </div>

        <div className="pricing-grid">
          {plans.map((p) => (
            <div key={p.key} className={`price-card ${p.featured ? "featured" : ""}`}>
              <div className="price-card-column">
                <h3>{p.name}</h3>
                <p className="price">
                  {p.price.display} <span className="price-sub">{p.price.sub}</span>
                </p>
                <p className="price-note">{p.note}</p>

                <ul>
                  {p.bullets.map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </div>

              <button onClick={p.onClick} type="button">
                {p.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="compare-wrap">
          <button className="compare-btn" type="button" onClick={() => setShowCompare((s) => !s)}>
            {showCompare ? "Hide plan details" : "Show plan details"}
          </button>

          {showCompare && (
            <div className="compare-table-wrap">
              <div className="compare-table">
                {/* Desktop header row only (CSS hides it on mobile) */}
                <div className="compare-row compare-head">
                  <div className="compare-cell feature">Feature</div>
                  <div className="compare-cell">Free</div>
                  <div className="compare-cell">Startup</div>
                  <div className="compare-cell">Business</div>
                  <div className="compare-cell">Agency</div>
                </div>

                {/* Rows */}
                {compareRows.map((r, idx) => (
                  <div className="compare-row" key={idx}>
                    <div className="compare-cell feature">{r.label}</div>

                    {/* ✅ data-label used by CSS to create Webflow-style mobile labels */}
                    <div className="compare-cell" data-label="Free">{r.free}</div>
                    <div className="compare-cell" data-label="Startup">{r.startup}</div>
                    <div className="compare-cell" data-label="Business">{r.business}</div>
                    <div className="compare-cell" data-label="Agency">{r.agency}</div>
                  </div>
                ))}

                <p className="compare-footnote">
                  * If you keep pages unlimited on paid plans, you can remove the “Extra pages” PAYG line for those tiers.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <h2>Start building with <span className="gradient-text">Kairos</span>.</h2>
        <p>Your next website is one conversation away.</p>
        <button 
          className="cta-btn"
          onClick={() => navigate("/authentication")}
        >Start chatting</button>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <img 
          onClick={() => {
            document.getElementById("top")?.scrollIntoView({ behavior: "smooth" });
          }}
          src={logo} 
          alt="Kairos Logo"
          className="footer-logo" 
        />
        <p>© 2026 Kairos AI. All rights reserved.</p>

        <p>Kairos Policies</p>

        {/* LEGAL LINKS (Landing page footer) */}
        <div className="legal-links">
              <button
                className="legal-link-btn"
                onClick={() => navigate("/privacy-policy")}
                type="button"
              >
                Privacy Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/terms-and-conditions")}
                type="button"
              >
                Terms & Conditions
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/cookie-policy")}
                type="button"
              >
                Cookie Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/refund-policy")}
                type="button"
              >
                Refund Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/acceptable-use-policy")}
                type="button"
              >
                Acceptable Use
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/ai-use-policy")}
                type="button"
              >
                AI Use & Content Policy
              </button>

              <button
                className="legal-link-btn"
                onClick={() => navigate("/subscription-policy")}
                type="button"
              >
                Subscription Policy
              </button>
            </div>
      </footer>

    </main>
  );
}

export default Home; // Keep default export for router
